import { config } from "@/config/env";
import { ProductsFilter } from "../types";

/**
 * Returns `true` if the page/tab should be **disabled** based on its product filter
 * vs the dealer's contracted products.
 *
 * Supported formats for `metadataProducts`:
 * - `string[]`           — legacy, treated as `$some` (backward compatible)
 * - `{ $some: [...] }`  — enabled if ANY listed product matches
 * - `{ $every: [...] }` — enabled only if ALL listed products match
 *
 * If no filter is set, the page/tab is always enabled (returns `false`).
 */
export function isDisabledByProducts(
    metadataProducts: ProductsFilter | undefined | null,
    contractedProducts: string[]
): boolean {
    if (!metadataProducts) return false;

    const lowerContracted = contractedProducts.map((p) => p.toLowerCase());

    // Legacy: plain array → treat as $some
    if (Array.isArray(metadataProducts)) {
        if (metadataProducts.length === 0) return false;
        return !metadataProducts.some((p) => lowerContracted.includes(p.toLowerCase()));
    }

    // $some: enabled if ANY product matches
    if ("$some" in metadataProducts) {
        const list = metadataProducts.$some;
        if (!list?.length) return false;
        return !list.some((p) => lowerContracted.includes(p.toLowerCase()));
    }

    // $every: enabled only if ALL products match
    if ("$every" in metadataProducts) {
        const list = metadataProducts.$every;
        if (!list?.length) return false;
        return !list.every((p) => lowerContracted.includes(p.toLowerCase()));
    }

    return false; // unknown operator → default enabled
}

/** Products whose incomplete buildout status should NOT disable pages/tabs. Driven by NEXT_PUBLIC_PAGE_BUILDOUT_EXCLUDED_PRODUCTS. */
export const PAGE_BUILDOUT_EXCLUDED_PRODUCTS = config.PAGE_BUILDOUT_EXCLUDED_PRODUCTS;

/**
 * Returns `true` if the page/tab should be **disabled** because its required product
 * has `BuildOutStatus === "IN"` (setup incomplete).
 *
 * Extracts all product names referenced in the filter and disables the page if any
 * of them appear in `incompleteProducts` (after applying `exclude` list).
 */
export function isDisabledByIncompleteProducts(
    metadataProducts: ProductsFilter | undefined | null,
    incompleteProducts: string[],
    exclude: string[] = []
): boolean {
    if (!metadataProducts || !incompleteProducts.length) return false;

    const lowerExclude = exclude.map((p) => p.toLowerCase());
    const filtered = incompleteProducts.filter((p) => !lowerExclude.includes(p.toLowerCase()));

    if (!filtered.length) return false;

    const lowerIncomplete = filtered.map((p) => p.toLowerCase());

    const list = Array.isArray(metadataProducts)
        ? metadataProducts
        : "$some" in metadataProducts
        ? metadataProducts.$some
        : "$every" in metadataProducts
        ? metadataProducts.$every
        : [];

    return list.some((p) => lowerIncomplete.includes(p.toLowerCase()));
}

export function getPageDisplayTitle(pageTitles: Record<string, string[]> | undefined, contractedProducts: string[] | undefined | null): string | undefined {
    if (!pageTitles) return undefined;
    const lowerContracted = (contractedProducts ?? []).map((p) => p.toLowerCase());
    let bestTitle: string | undefined;
    let bestCount = -1;
    for (const [title, required] of Object.entries(pageTitles)) {
        // Empty array = catch-all fallback (lowest priority, wins only if no specific rule matches)
        if (required.length === 0) {
            if (bestCount < 0) { bestTitle = title; bestCount = 0; }
            continue;
        }
        const allMatch = required.every((p) => lowerContracted.includes(p.toLowerCase()));
        if (allMatch && required.length > bestCount) {
            bestTitle = title;
            bestCount = required.length;
        }
    }
    if (bestTitle === "") return undefined; // "" = explicit "keep original title" sentinel
    return bestTitle;
}
